import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { gruposBreadcrumbItems } from "data/grupos";
import { faFilter, faPlus, faTimes, faCheck, faUserSlash, faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchBox from "components/common/SearchBox";
import { Card, Badge, Spinner } from "react-bootstrap";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState, ChangeEvent } from "react";
import FilterOffCanvas, { FilterOption } from "components/common/FilterOffCanvas";
import { useGrupos, ActiveFilters } from "services/grupo";
import GruposTable, { grupoTableColumns } from "components/tables/GruposTable";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import axios from "axios";
import { useToast } from "providers/ToastProvider";

// Interface para o estado de filtros consolidado
interface FilterState {
  active: ActiveFilters;
  pending: ActiveFilters | null;
  filtersPending: boolean;
}

const Grupos = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showFilterCanvas, setShowFilterCanvas] = useState<boolean>(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { showToast } = useToast();

  // Estado consolidado para filtros
  const [filterState, setFilterState] = useState<FilterState>({
    active: { groupStatus: "active" },
    pending: null,
    filtersPending: false,
  });

  // Opções de filtro para o componente FilterOffCanvas
  const filterOptions: FilterOption[] = [
    {
      id: "groupStatus",
      label: "Status do Grupo",
      type: "radioCard",
      options: [
        {
          value: "active",
          label: "Ativos",
          description: "Apenas grupos ativos",
          icon: faCheck,
        },
        {
          value: "deleted",
          label: "Excluídos",
          description: "Apenas grupos excluídos",
          icon: faUserSlash,
        },
        {
          value: "all",
          label: "Todos",
          description: "Ativos e excluídos",
          icon: faUsers,
        },
      ],
    },
  ];

  const gruposQuery = useGrupos(
    searchTerm,
    filterState.active,
    pagination.pageIndex + 1,
    pagination.pageSize,
  );

  // Conta quantos filtros estão ativos
  const activeFilterCount = filterState.active.groupStatus !== "active" ? 1 : 0;

  // Explicitly refetch when pagination changes
  useEffect(() => {
    gruposQuery.refetch();
  }, [pagination.pageIndex, pagination.pageSize]);


  // Reset pagination when search term or filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchTerm, filterState.active]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleApplyFilters = (filters: Record<string, any>) => {
    // Apply filters immediately
    setFilterState((prev) => ({
      ...prev,
      active: filters as ActiveFilters,
      pending: null,
      filtersPending: false,
    }));

    // Reset to first page when applying new filters
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));

    // Close the filter canvas
    setShowFilterCanvas(false);

    // Refetch data with new filters
    gruposQuery.refetch();
  };

  const handleClearFilters = () => {
    setFilterState((prev) => ({
      ...prev,
      active: { groupStatus: "active" },
      pending: null,
      filtersPending: false,
    }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    gruposQuery.refetch();
  };

  const table = useReactTable({
    data: gruposQuery.data?.data || [],
    columns: grupoTableColumns as any,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: gruposQuery.data?.total
      ? Math.ceil(gruposQuery.data.total / pagination.pageSize)
      : 1,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  if (gruposQuery.isLoading) {
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );
  }

  // Check for access denied (403 error)
  if (gruposQuery.error) {
    const error = gruposQuery.error as any;
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return (
        <div>
          <PageBreadcrumb items={gruposBreadcrumbItems} />
          <div className="mb-9">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0">Grupos de Acesso</h2>
              <Button
                variant="primary"
                as={Link}
                to="/cadastros/grupos/novo"
                className="px-4 py-2"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Novo Grupo
              </Button>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2 flex-grow-1">
                <SearchBox
                  placeholder="Pesquisar grupos por código ou descrição..."
                  onChange={handleSearchInputChange}
                  className="flex-grow-1"
                  disabled={true}
                />
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                  className="px-3 py-2 d-flex align-items-center"
                  onClick={() => setShowFilterCanvas(true)}
                  disabled={true}
                >
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  Filtros
                </Button>
              </div>
            </div>

            <AccessDeniedOverlay message="Você não tem permissão para consultar ou visualizar grupos de acesso.">
              <Card className="border-0 shadow-sm" style={{
                filter: "blur(8px)",
                pointerEvents: "none",
                userSelect: "none",
              }}>
                <Card.Body className="p-0">
                  <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Código</th>
                            <th>Descrição</th>
                            <th>Permissões</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array(5)
                            .fill(0)
                            .map((_, i) => (
                              <tr key={i}>
                                <td>{i + 100}</td>
                                <td>Grupo Exemplo</td>
                                <td>5 permissões</td>
                                <td>-</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </AccessDeniedOverlay>
          </div>
        </div>
      );
    }

    return (
      <div className="alert alert-danger">
        Erro ao carregar grupos:{" "}
        {(gruposQuery.error as Error).message || "Ocorreu um erro desconhecido."}
      </div>
    );
  }

  // Display error message if isSuccess is false
  if (gruposQuery.data && !gruposQuery.data.isSuccess) {
    return (
      <div className="alert alert-danger">
        Erro ao carregar grupos:{" "}
        {gruposQuery.data.message || "Ocorreu um erro desconhecido."}
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={gruposBreadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Grupos de Acesso</h2>
          <Button
            variant="primary"
            as={Link}
            to="/cadastros/grupos/novo"
            className="px-4 py-2"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Novo Grupo
          </Button>
        </div>

        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2 flex-grow-1">
            <SearchBox
              placeholder="Pesquisar grupos por código ou descrição..."
              onChange={handleSearchInputChange}
              className="flex-grow-1"
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            {activeFilterCount > 0 && (
              <Badge
                bg="light"
                text="dark"
                className="d-flex align-items-center py-2 px-3 border shadow-sm"
                style={{ borderRadius: "50px" }}
              >
                <span>
                  {filterState.active.groupStatus === "deleted" &&
                    "Mostrando excluídos"}
                  {filterState.active.groupStatus === "all" && "Mostrando todos"}
                </span>
                <Button
                  variant="link"
                  className="p-0 ms-2 text-body-secondary border-0"
                  onClick={handleClearFilters}
                  style={{ fontSize: "14px" }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </Badge>
            )}

            <Button
              variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
              className="px-3 py-2 d-flex align-items-center"
              onClick={() => setShowFilterCanvas(true)}
            >
              <FontAwesomeIcon icon={faFilter} className="me-2" />
              Filtros
              {activeFilterCount > 0 && (
                <Badge
                  bg="light"
                  text="primary"
                  className="ms-2 d-flex align-items-center justify-content-center"
                  style={{ borderRadius: "50%", width: "20px", height: "20px" }}
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <GruposTable />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>
      </div>

      <FilterOffCanvas
        show={showFilterCanvas}
        onHide={() => setShowFilterCanvas(false)}
        onApplyFilters={handleApplyFilters}
        title="Filtros de Grupos"
        filterOptions={filterOptions}
        activeFilters={filterState.active}
      />
    </div>
  );
};
export default Grupos;
